import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import { http } from "@/lib/api";

/**
 * "Découverte" section for the homepage — 3 latest videos with strong CTA
 * toward the /transmissions hub. Auto-hidden if no videos exist yet.
 */
export function LatestVideosSection() {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        http.get("/videos", { params: { limit: 3 } })
            .then(({ data }) => setVideos(data || []))
            .catch(() => setVideos([]))
            .finally(() => setLoading(false));
    }, []);

    if (loading || videos.length === 0) return null;

    return (
        <section className="py-24 md:py-32 bg-primary/5 border-y border-primary/10" data-testid="latest-videos-section">
            <div className="max-w-6xl mx-auto px-6">
                <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
                    <div>
                        <div className="text-xs tracking-[0.35em] uppercase text-primary mb-3 font-medium flex items-center gap-2">
                            <Play className="w-3.5 h-3.5" /> Découverte
                        </div>
                        <h2 className="font-serif text-3xl sm:text-4xl leading-tight">
                            Dernières transmissions vidéo
                        </h2>
                        <p className="mt-3 text-foreground/70 max-w-xl">
                            Messages canalisés, méditations guidées et enseignements sur le langage de lumière — à recevoir en son et en présence.
                        </p>
                    </div>
                    <Link
                        to="/transmissions"
                        className="text-sm text-primary hover:underline inline-flex items-center gap-1 font-medium whitespace-nowrap"
                        data-testid="see-all-videos"
                    >
                        Toutes les vidéos <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>

                <div className="grid gap-6 md:grid-cols-3">
                    {videos.map((v, i) => (
                        <motion.article
                            key={v.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            className="group rounded-3xl overflow-hidden bg-card border border-border/60 hover:border-primary/40 transition-colors shadow-sm hover:shadow-md flex flex-col"
                            data-testid={`home-video-${v.id}`}
                        >
                            <Link to={`/transmissions/${v.slug}`} className="block relative aspect-video overflow-hidden bg-black">
                                {v.thumbnail_url && (
                                    <img src={v.thumbnail_url} alt={v.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                )}
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center">
                                        <Play className="w-7 h-7 text-primary translate-x-0.5" fill="currentColor" />
                                    </div>
                                </div>
                                {v.duration_human && (
                                    <div className="absolute bottom-3 right-3 text-xs bg-black/70 text-white px-2 py-0.5 rounded font-mono">
                                        {v.duration_human}
                                    </div>
                                )}
                            </Link>
                            <div className="p-5">
                                <h3 className="font-serif text-lg leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                                    <Link to={`/transmissions/${v.slug}`}>{v.title}</Link>
                                </h3>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
}
